import { type KeyboardEvent } from 'react'
import { clsx } from 'clsx'

import { useView, type View } from '../views/ViewContext'

const VIEWS: ReadonlyArray<{ value: View; label: string }> = [
  { value: 'local', label: 'Local' },
  { value: 'tourism', label: 'Tourism' },
] as const

export function ViewToggle() {
  const { view, setView } = useView()

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    const i = VIEWS.findIndex(v => v.value === view)
    const nextIdx =
      event.key === 'ArrowRight'
        ? (i + 1) % VIEWS.length
        : (i - 1 + VIEWS.length) % VIEWS.length
    setView(VIEWS[nextIdx]!.value)
  }

  return (
    <div
      role="tablist"
      aria-label="View"
      // PORTED — FgViewToggle (designs/system.jsx ~361), derived from 857:958:
      // track bg-sunken at radius 24, padding 4/8. Only the styling moved here;
      // the behaviour below is ADR-0002's locked contract and is untouched.
      className="inline-flex items-center gap-0.5 rounded-3xl bg-bg-sunken px-2 py-1 text-sm"
    >
      {VIEWS.map(item => {
        const isActive = item.value === view
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => setView(item.value)}
            onKeyDown={onKeyDown}
            className={clsx(
              // PORTED — 857:958: the selected side is an accent pill at radius
              // 16 with 24/10 padding; the unselected side is transparent at
              // radius 24. Note the radius differs by state upstream, which is
              // itself a non-colour cue, and it is kept.
              'px-4 py-1.5 transition-colors focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
              isActive
                // The selected side changes FILL and WEIGHT, not just tint —
                // colour is never the only signal (CLAUDE.md), and after T02 the
                // accent and the track are both green-tinted, so a tint-only
                // difference would be weak. font-medium against font-normal.
                ? 'rounded-r3 bg-accent font-medium text-accent-on'
                : 'rounded-3xl bg-transparent font-normal text-accent-ink hover:bg-bg-app',
            )}
          >
            {item.label}
          </button>
        )
      })}
    </div>
  )
}
